import { element } from "./dom.js";
import {
  markedPinyin,
  numberedPinyin,
  type PinyinItem,
  syllableHalves,
} from "./pinyin-worksheet-words.js";
import type { GlyphLibrary, SquareContent } from "./worksheet-drawing.js";
import { blockGap, paginate } from "./worksheet-layout.js";
import {
  drawPages,
  pageRoom,
  partHeadHeight,
  type SheetLabels,
  type SheetPart,
  sized,
} from "./worksheet-page.js";
import { entryAddress, shortMeaning } from "./worksheet-sheet.js";
import type {
  PinyinGap,
  PinyinWorksheetState,
} from "./pinyin-worksheet-state.js";
import { seededToss } from "./worksheet-seed.js";

/**
 * A pinyin sheet, as pages ready to print.
 *
 * Up to six parts, each starting on a page of its own and each asked for
 * separately. Four ask the same words four ways: numbered pinyin to write
 * with tone marks, 汉字 to write the pinyin for, pinyin with the initial or
 * the final of each syllable left out, and pinyin to write the 汉字 for. A
 * page of empty four-line paper follows them, and the answers come last. A
 * word has the same number in every part, so one answer key serves all four.
 *
 * Every answer is the 词典's. The numbered pinyin is numbered from the tones
 * the entry colours its reading by, and the answer key prints the entry's
 * readings, so a 多音字 is read the way its entry reads it.
 */

/** What drawing a pinyin sheet needs. */
export interface PinyinSheetProperties {
  readonly library: GlyphLibrary;
}

/**
 * The bands of 四线三格, the four-line paper pinyin is taught on.
 *
 * The middle band holds a letter's body and the bands above and below hold
 * what rises and falls from it, the tone mark among what rises. 3.5 mm a band
 * is the size of an adult's handwriting, smaller than a children's exercise
 * book prints it.
 */
export const pinyinBand = 3.5;

/** The four lines' height, three bands. */
export const fourLineHeight = 3 * pinyinBand;

/** The line over each question, its number and what it asks about. */
export const promptHeight = 7;

/** The same line where it holds 汉字, which are printed larger. */
export const hanziPromptHeight = 9;

/** The side the squares for writing 汉字 aim for. */
export const hanziSquare = 14;

/** One line of the answer key. */
export const keyLineHeight = 6;

/** The most syllables or characters a question has and stays three to a row. */
const narrowLength = 4;

/** Columns for questions whose longest answer is so many letters long. */
export function questionColumns(longest: number): number {
  return longest > narrowLength ? 2 : 3;
}

/**
 * The size pinyin is printed at on the four lines, in millimetres. A lower
 * case letter's body fills the middle band, as a learner's should.
 */
const printedPinyin = 6.5;

/**
 * The width of a gap, one for every initial and one for every final.
 *
 * A gap sized to its own answer would say how many letters go in it. So
 * every initial's gap holds the longest initial, zh, ch or sh, and every
 * final's holds a four-letter final such as uāng printed at full size.
 */
export const gapWidths: Readonly<Record<"initial" | "final", number>> = {
  initial: 9,
  final: 16,
};

/** The space between one syllable and the next. */
const syllableSpace = 4;

/** The space between a gap and the letters beside it, and its tone. */
const gapSpace = 0.8;

/** The width of the tone number after a gapped final. */
const toneWidth = 2;

/**
 * Roughly how wide some letters print, in ems, since nothing can measure
 * text before it is laid out. A guess on the wide side costs a little space,
 * where one on the narrow side would print a letter over a gap.
 */
function printedWidth(letters: string): number {
  let ems = 0;
  for (const letter of letters.normalize("NFD").replace(/\p{M}/gu, "")) {
    ems += /[ijlrtf]/u.test(letter)
      ? 0.34
      : /[mwMW]/u.test(letter)
        ? 0.86
        : /\p{Lu}/u.test(letter)
          ? 0.7
          : 0.58;
  }

  return ems * printedPinyin;
}

/** Which half of a syllable a gap takes, or none for one printed whole. */
type Gapped = "initial" | "final" | undefined;

/**
 * One syllable of the initials and finals part: the letters printed and the
 * half left out.
 */
interface GappedSyllable {
  readonly initial: string;
  readonly final: string;
  readonly tone: number;
  readonly gap: Gapped;
}

/**
 * The half of each syllable of a word that the sheet leaves out.
 *
 * A syllable with no initial is asked its final, which is all of it, and one
 * that is not a pinyin syllable is printed whole. Otherwise the setting
 * decides, and Mixed tosses a coin keyed by the seed, the word and the
 * syllable, so a reprint leaves the same gaps and a reshuffle new ones.
 */
export function gappedSyllables(
  item: PinyinItem,
  gap: PinyinGap,
  seed: string,
): readonly GappedSyllable[] {
  return item.reading.syllables.map((syllable, at) => {
    const tone = item.reading.tones[at] ?? 5;
    const halves = syllableHalves(syllable);
    if (halves === undefined) {
      return { initial: "", final: syllable, tone, gap: undefined };
    }
    const left: Gapped =
      halves.initial === "" || gap === "finals"
        ? "final"
        : gap === "initials"
          ? "initial"
          : seededToss(`${seed}:${item.entry.word}:${String(at)}`)
            ? "initial"
            : "final";

    return { ...halves, tone, gap: left };
  });
}

/** How wide a word's syllables print, gaps included. */
function gappedWidth(syllables: readonly GappedSyllable[]): number {
  return syllables.reduce(
    (sum, syllable, at) =>
      sum +
      (at === 0 ? 0 : syllableSpace) +
      (syllable.gap === "initial"
        ? gapWidths.initial + gapSpace + printedWidth(syllable.final)
        : syllable.gap === "final"
          ? printedWidth(syllable.initial) +
            (syllable.initial === "" ? 0 : gapSpace) +
            gapWidths.final +
            gapSpace +
            toneWidth
          : printedWidth(syllable.final)),
    0,
  );
}

/** The lines of 四线三格, as wide as asked. */
function fourLines(document: Document, width: number): SVGElement {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "cb-pw-lines");
  svg.setAttribute("viewBox", `0 0 ${String(width)} ${String(fourLineHeight)}`);
  svg.setAttribute("width", `${String(width)}mm`);
  svg.setAttribute("height", `${String(fourLineHeight)}mm`);
  svg.setAttribute("aria-hidden", "true");
  for (let line = 0; line < 4; line += 1) {
    const y = Math.min(fourLineHeight - 0.1, Math.max(0.1, line * pinyinBand));
    const drawn = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "line",
    );
    // The third line is the one letters sit on, so it is the darker.
    for (const [name, value] of Object.entries({
      x1: "0",
      x2: String(width),
      y1: String(y),
      y2: String(y),
      stroke: line === 2 ? "#7a7a7a" : "#b8b8b8",
      "stroke-width": "0.2",
    })) {
      drawn.setAttribute(name, value);
    }
    svg.append(drawn);
  }

  return svg;
}

/**
 * A word's pinyin printed on four lines with its gaps left open.
 *
 * Each gap is a box over all three bands, in the red the other sheets mark
 * where a learner writes. A gapped final has its tone's number after it, 5 for the neutral
 * tone, since the mark it would carry is part of what was taken out.
 * Printed letters sit on the third line, the tone number on the second.
 */
function gappedLines(
  document: Document,
  width: number,
  syllables: readonly GappedSyllable[],
): SVGElement {
  const needed = gappedWidth(syllables);
  const svg = fourLines(document, Math.max(width, needed));
  // A word too long even for a row of its own prints smaller, lines and all.
  svg.setAttribute("width", `${String(width)}mm`);
  svg.setAttribute(
    "height",
    `${String((fourLineHeight * width) / Math.max(width, needed))}mm`,
  );
  svg.setAttribute("class", "cb-pw-lines cb-pw-gapped");
  svg.setAttribute("lang", "zh-Latn-CN-pinyin");
  svg.removeAttribute("aria-hidden");
  const add = (
    name: string,
    attributes: Record<string, string>,
    text = "",
  ): void => {
    const drawn = document.createElementNS("http://www.w3.org/2000/svg", name);
    for (const [key, value] of Object.entries(attributes)) {
      drawn.setAttribute(key, value);
    }
    drawn.textContent = text;
    svg.append(drawn);
  };
  const letters = (x: number, text: string): number => {
    if (text !== "") {
      add(
        "text",
        {
          class: "cb-pw-given",
          x: String(x),
          y: String(2 * pinyinBand),
          "font-size": String(printedPinyin),
        },
        text,
      );
    }

    return x + printedWidth(text);
  };
  const box = (x: number, half: "initial" | "final"): number => {
    add("rect", {
      class: "cb-pw-gap",
      x: String(x),
      y: "0.4",
      width: String(gapWidths[half]),
      height: String(fourLineHeight - 0.8),
      fill: "none",
      // The faint red of 描红 the other sheets mark their answer boxes in,
      // `$tracing-line` in `worksheet.scss`.
      stroke: "#ef9f9a",
      "stroke-width": "0.3",
    });

    return x + gapWidths[half];
  };

  let x = 0;
  for (const [at, syllable] of syllables.entries()) {
    x += at === 0 ? 0 : syllableSpace;
    if (syllable.gap === "initial") {
      x = letters(box(x, "initial") + gapSpace, syllable.final);
    } else if (syllable.gap === "final") {
      x = letters(x, syllable.initial);
      x = box(x + (syllable.initial === "" ? 0 : gapSpace), "final") + gapSpace;
      add(
        "text",
        {
          class: "cb-pw-tone",
          x: String(x),
          y: String(pinyinBand),
          "font-size": String(printedPinyin / 2),
        },
        String(syllable.tone),
      );
      x += toneWidth;
    } else {
      x = letters(x, syllable.final);
    }
  }

  return svg;
}

/** What the initials and finals part tells a learner to do. */
const splitInstructions: Readonly<Record<PinyinGap, string>> = {
  mixed:
    "Fill each box with the missing initial or final, or the whole syllable if it has no initial. Use the tone numbers to add tone marks to finals and whole syllables (5 means neutral tone, with no mark).",
  initials:
    "Write the missing initials, or the whole syllable if it has no initial. Add tone marks to whole syllables (5 means neutral tone, with no mark).",
  finals:
    "Write the missing finals, or the whole syllable if it has no initial. Add tone marks using the tone numbers after the boxes. 5 means neutral tone (no mark).",
};

/** Draws pinyin sheets. */
export class PinyinSheet {
  readonly #library: GlyphLibrary;

  constructor(properties: PinyinSheetProperties) {
    this.#library = properties.library;
  }

  /**
   * Every page of the sheet, leaving out the parts not asked for.
   * @param items - The words, in the order they are numbered.
   */
  pages(
    state: PinyinWorksheetState,
    items: readonly PinyinItem[],
    labels: SheetLabels,
  ): readonly HTMLElement[] {
    const room = pageRoom(state.paper);
    const parts: SheetPart[] = [];
    const longestReading = Math.max(
      0,
      ...items.map((item) => item.reading.syllables.length),
    );
    const longestWord = Math.max(
      0,
      ...items.map((item) => Array.from(item.entry.word).length),
    );

    const questions = (
      head: Omit<SheetPart, "pages">,
      columns: number,
      cellHeight: number,
      cell: (item: PinyinItem, number: number, width: number) => HTMLElement,
      alone: (item: PinyinItem) => boolean = () => false,
    ): void => {
      const rows: HTMLElement[] = [];
      let waiting: HTMLElement[] = [];
      const row = (cells: readonly HTMLElement[]): void => {
        rows.push(
          sized(element("div", { className: "cb-pw-row", children: cells }), {
            height: cellHeight,
          }),
        );
      };
      for (const [at, item] of items.entries()) {
        // A question too wide for a column has a row to itself, after the
        // ones before it.
        if (alone(item)) {
          if (waiting.length > 0) {
            row(waiting);
            waiting = [];
          }
          row([sized(cell(item, at + 1, room.width), { width: room.width })]);
          continue;
        }
        const width = room.width / columns;
        waiting.push(sized(cell(item, at + 1, width), { width }));
        if (waiting.length === columns) {
          row(waiting);
          waiting = [];
        }
      }
      if (waiting.length > 0) {
        row(waiting);
      }
      parts.push({
        ...head,
        pages: this.#paged(
          rows,
          rows.map(() => cellHeight),
          room.height - partHeadHeight(head),
          room.height,
        ),
      });
    };

    if (state.tones) {
      questions(
        {
          title: `Tone marks: ${labels.title}`,
          instructions:
            "Rewrite each word with tone marks. Tone numbers follow each syllable. 5 means neutral tone (no mark).",
        },
        questionColumns(longestReading),
        promptHeight + fourLineHeight + blockGap,
        (item, number, width) =>
          this.#question(number, width, {
            asked: numberedPinyin(item.reading),
            lang: "zh-Latn-CN-pinyin",
            meaning: item.entry.meaning,
            answer: fourLines(document, width - 4),
          }),
      );
    }
    if (state.pinyin) {
      questions(
        {
          title: `Pinyin: ${labels.title}`,
          instructions: "Write the pinyin for each word. Include tone marks.",
        },
        questionColumns(longestReading),
        hanziPromptHeight + fourLineHeight + blockGap,
        (item, number, width) =>
          this.#question(number, width, {
            asked: item.entry.word,
            lang: "zh-Hans",
            meaning: item.entry.meaning,
            answer: fourLines(document, width - 4),
            large: true,
          }),
      );
    }
    if (state.split) {
      const gapped = items.map((item) =>
        gappedSyllables(item, state.gap, state.seed),
      );
      const rowOf = new Map(items.map((item, at) => [item, gapped[at]!]));
      // A word too wide for two to a row has a row of its own. The others
      // go three to a row where all of them fit, and otherwise two.
      const fits = (item: PinyinItem, columns: number): boolean =>
        gappedWidth(rowOf.get(item)!) <= room.width / columns - 4;
      const columns = items.every((item) => !fits(item, 2) || fits(item, 3))
        ? 3
        : 2;
      questions(
        {
          title: `Initials and finals: ${labels.title}`,
          instructions: splitInstructions[state.gap],
        },
        columns,
        hanziPromptHeight + fourLineHeight + blockGap,
        (item, number, width) =>
          this.#question(number, width, {
            asked: item.entry.word,
            lang: "zh-Hans",
            meaning: item.entry.meaning,
            answer: gappedLines(document, width - 4, rowOf.get(item)!),
            large: true,
          }),
        (item) => !fits(item, 2),
      );
    }
    if (state.hanzi) {
      const columns = questionColumns(longestWord);
      const square = Math.min(
        hanziSquare,
        (room.width / columns - 4) / Math.max(1, longestWord),
      );
      questions(
        {
          title: `Characters: ${labels.title}`,
          instructions:
            "Write the characters for each word. Use one square per character.",
        },
        columns,
        promptHeight + square + blockGap,
        (item, number, width) =>
          this.#question(number, width, {
            asked: markedPinyin(item.reading),
            lang: "zh-Latn-CN-pinyin",
            meaning: item.entry.meaning,
            answer: this.#library.row(
              square,
              Array.from(item.entry.word, (): SquareContent => ({
                kind: "empty",
              })),
            ),
          }),
      );
    }
    if (state.lines) {
      const head: Omit<SheetPart, "pages"> = { title: "Pinyin writing paper" };
      const height = fourLineHeight + 2 * blockGap;
      const count = Math.floor((room.height - partHeadHeight(head)) / height);
      parts.push({
        ...head,
        pages: [
          Array.from({ length: count }, () =>
            sized(
              element("div", {
                className: "cb-pw-paper-line",
                children: [fourLines(document, room.width)],
              }),
              { height },
            ),
          ),
        ],
      });
    }
    if (
      state.key &&
      (state.tones || state.pinyin || state.split || state.hanzi)
    ) {
      const head = {
        title: `Answers: ${labels.title}`,
        instructions:
          "Pinyin comes from the Chinese Boost dictionary. For a character entered on its own, the exercise uses the first reading listed.",
      };
      const columns = 3;
      const width = room.width / columns;
      const rows: HTMLElement[] = [];
      for (let at = 0; at < items.length; at += columns) {
        rows.push(
          sized(
            element("div", {
              className: "cb-pw-row",
              children: items.slice(at, at + columns).map((item, offset) =>
                sized(this.#answer(item, at + offset + 1, labels.origin), {
                  width,
                }),
              ),
            }),
            { height: keyLineHeight },
          ),
        );
      }
      parts.push({
        ...head,
        pages: this.#paged(
          rows,
          rows.map(() => keyLineHeight),
          room.height - partHeadHeight(head),
          room.height,
        ),
      });
    }

    return drawPages(state.paper, parts, labels);
  }

  #paged(
    rows: readonly HTMLElement[],
    heights: readonly number[],
    first: number,
    rest: number,
  ): (readonly Node[])[] {
    return paginate(heights, { first, rest }).map((page) =>
      page.map((at) => rows[at]!),
    );
  }

  #question(
    number: number,
    width: number,
    question: {
      readonly asked: string;
      readonly lang: string;
      readonly meaning: string;
      readonly answer: Element;
      readonly large?: boolean;
    },
  ): HTMLElement {
    const prompt = sized(
      element("p", {
        className: "cb-pw-prompt",
        children: [
          element("span", {
            className: "cb-pw-number",
            text: `${String(number)}.`,
          }),
          element("span", {
            className:
              question.large === true ? "cb-pw-asked-hanzi" : "cb-pw-asked",
            text: question.asked,
            attributes: { lang: question.lang },
          }),
          element("span", {
            className: "cb-pw-meaning",
            text: shortMeaning(question.meaning),
          }),
        ],
      }),
      {
        height: question.large === true ? hanziPromptHeight : promptHeight,
        width: width - 4,
      },
    );

    return element("div", {
      className: "cb-pw-question",
      children: [prompt, question.answer],
    });
  }

  #answer(item: PinyinItem, number: number, origin: string): HTMLElement {
    return element("a", {
      className: "cb-pw-answer",
      attributes: { href: entryAddress(origin, item.entry.word) },
      children: [
        element("span", {
          className: "cb-pw-number",
          text: `${String(number)}.`,
        }),
        element("span", {
          className: "cb-pw-answer-hanzi",
          text: item.entry.word,
          attributes: { lang: "zh-Hans" },
        }),
        element("span", {
          className: "cb-pw-answer-pinyin",
          text: item.entry.pinyin,
          attributes: { lang: "zh-Latn-CN-pinyin" },
        }),
      ],
    });
  }
}
